import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { roleLandingPath } from '../utils/appRoles';
import BrandLogo from '../components/BrandLogo';
import RoleSwitcher from '../components/landing/RoleSwitcher';
import DigikocBand from '../components/landing/DigikocBand';
import ClosingCta, { LandingCta } from '../components/landing/ClosingCta';
import LandingFooter from '../components/landing/LandingFooter';
import '../components/landing/landing.css';

const TITLE = 'eis ekosis LMS | Dijital eğitim platformu';

/**
 * Herkese açık karşılama sayfası (/hosgeldin). Oturumsuz ziyaretçi kök adrese
 * (/) geldiğinde buraya yönlendirilir; sayfanın işi uygulamayı iki cümlede
 * anlatıp "Giriş Yap"a götürmek (üst çubuktaki dolu buton; hero'da CTA yok).
 * Giriş /login'de yapılır — orası tek kapıyı (eis ekosis ile Giriş Yap) ve hata
 * mesajlarını zaten yönetiyor.
 *
 * Hesap açma yok: hesaplar okul/kurum tarafından açılıyor, giriş SSO. Bu yüzden
 * bilerek "Kayıt Ol" butonu yok.
 *
 * Bu sayfa hiçbir şey saklamaz (localStorage/sessionStorage/çerez yok) — çerez
 * envanteri (legal.inventory.ts) bu yüzden değişmedi.
 */
const Landing: React.FC = () => {
  const { user } = useAuth();

  // Başlık ve dil: sayfadan çıkınca eski değerlerine dönsün.
  useEffect(() => {
    const prevTitle = document.title;
    const prevLang = document.documentElement.lang;
    document.title = TITLE;
    document.documentElement.lang = 'tr';
    return () => {
      document.title = prevTitle;
      document.documentElement.lang = prevLang;
    };
  }, []);

  const cta: LandingCta = user
    ? { to: roleLandingPath(user.role), label: 'Panele git' }
    : { to: '/login', label: 'Giriş Yap' };

  return (
    <div className="lp">
      <header className="lp-wrap lp-top">
        <Link to="/hosgeldin" className="lp-top__brand" aria-label="eis ekosis LMS ana sayfa">
          <BrandLogo variant="full" tone="color" className="lp-top__logo" />
        </Link>
        <nav className="lp-top__nav" aria-label="Hızlı erişim">
          {/* Tam ifade her ekranda aria-label'da; görünen metin dar ekranda kısalıyor. */}
          <Link
            to="/deneme-sinavi-videolari"
            className="lp-link lp-top__free"
            aria-label="Ücretsiz deneme sınavı videoları"
          >
            <span className="lp-top__free-long">Ücretsiz deneme sınavı videoları</span>
            <span className="lp-top__free-short" aria-hidden>
              Ücretsiz videolar
            </span>
          </Link>
          {/* Hero'da CTA kalmadı: giriş kapısı burası. Oturum açıksa "Panele git". */}
          <Link to={cta.to} className="lp-btn lp-btn--primary">
            {cta.label}
          </Link>
        </nav>
      </header>

      <main>
        <section className="lp-hero" aria-labelledby="lp-title">
          <div className="lp-wrap lp-hero__grid">
            <div className="lp-robot-wrap">
              <img
                className="lp-robot"
                src="/a-mascot.png"
                alt="Mezuniyet kepli, kulaklıklı ve boynunda düdük asılı beyaz maskot robot, ellerini beline koymuş gülümsüyor."
                width={1286}
                height={1300}
                decoding="async"
                fetchPriority="high"
                draggable={false}
              />
            </div>

            <div className="lp-talk">
              <div className="lp-bubble">
                <div className="lp-bubble__bg" aria-hidden>
                  <svg className="lp-bubble__tail" viewBox="0 0 56 44" focusable="false">
                    <path d="M56 3 C38 4 15 14 1 38 C20 40 40 38 56 31 Z" />
                  </svg>
                </div>
                <h1 id="lp-title" className="lp-h1">
                  Selam! Kitapların, ödevlerin ve hedeflerin burada.
                </h1>
                <p className="lp-lede">
                  eis ekosis LMS; dijital kitapları ve çözüm videolarını, ödevleri, çalışma
                  programını ve performans takibini tek uygulamada toplar.
                </p>
              </div>
            </div>
          </div>
        </section>

        <div className="lp-table">
          <RoleSwitcher />
          <DigikocBand />
          <ClosingCta cta={cta} signedIn={!!user} />
        </div>
      </main>

      <LandingFooter />
    </div>
  );
};

export default Landing;
